import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
    TDocumentDefinitions,
    Content
} from "pdfmake/interfaces";
import { STORE_CODE_MAP } from "../pages/Operations/PrintLabel";

pdfMake.vfs = pdfFonts.vfs;

type Product = {
    style_no: string;
    cost: number;
    msrp: number;
    description: string;
    sizes: string[];
    totalQtyReceived: number;
    isOverstock: boolean;
    stores: string[];
};

const generateCode = (orignalCost: number): string => {

    // Proceed with existing logic to calculate the code
    const date = new Date();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    let cost: string = '';
    const costValue = parseFloat(orignalCost.toString());
    if (isNaN(costValue)) {
        return 'Cost is not a number';
    } else {
        if (costValue < 1) {
            // For costs less than $1
            cost = Math.round(costValue * 100).toString().padStart(4, '0');
            cost = cost.slice(0, 2) + '.' + cost.slice(2);
        } else if (!Number.isInteger(costValue)) {
            // For decimal costs
            const cent = Math.round(costValue * 100);
            cost = cent.toString().padStart(4, '0');
        } else {
            // For integer costs
            cost = ((costValue * 100) / 100).toString().padStart(4, '0');
        }
    }

    const formattedCode = `${month.toString().padStart(2, '0')}${year.toString().slice(-2)}${cost.padStart(4, '0')}`;
    return formattedCode;
};
export const sampleProduct = (data: Product[]) => {
    const content: Content[] = [];
    for (let i = 0; i < data.length; i += 3) {
        const chunk = data.slice(i, i + 3);
        const isLastChunk = i + 3 >= data.length;
        content.push({
            table: {
                widths: [0],
                body: chunk.map(item => [buildFullWidthBlock(item)])
            },
            layout: {
                hLineWidth: (i: number, node: any) => 0,
                vLineWidth: () => 0
            },
            // pageBreak: (i + 1) % 4 === 0 ? "after" : undefined,
            // pageBreak: i !== data.length - 1 ? "after" : undefined,
            pageBreak: !isLastChunk ? "after" : undefined,
            margin: [0, -10, 0, 0]
        });
    }

    const docDefinition: TDocumentDefinitions = {
        pageSize: "LETTER",
        pageOrientation: "portrait",
        pageMargins: [25, 25, 25, 25],
        content
    };

    pdfMake.createPdf(docDefinition).open();
};

const buildFullWidthBlock = (item?: Product): Content => {
    if (!item) return { text: "" };

    const storeList = Object.values(STORE_CODE_MAP);
    const buildStoreRows = (
        stores: string[],
        renderStore: (store: string) => Content,
        perRow: number = 6
    ): Content[] => {

        const rows: Content[] = [];

        for (let i = 0; i < stores.length; i += perRow) {
            rows.push({
                columns: stores
                    .slice(i, i + perRow)
                    .map(store => renderStore(store))  // ✅ no spreading
            });
        }

        return rows;
    };

    const renderStore = (store: string): Content => {
        const selected = item.stores.includes(store);

        const outerCircle = {
            type: "ellipse" as const,
            x: 6,
            y: 6,
            r1: 6,
            r2: 6,
            lineWidth: 1
        };

        const innerCircle = {
            type: "ellipse" as const,
            x: 6,
            y: 6,
            r1: 3,
            r2: 3,
            color: "black"
        };

        return {
            columns: [
                {
                    width: 14,
                    canvas: selected
                        ? [outerCircle, innerCircle]
                        : [outerCircle],
                },
                {
                    text: store,
                    fontSize: 13,
                    bold: selected ? true : false,
                    color: selected ? 'black' : 'gray',
                }
            ],
            margin: [0, 1, 0, 1]
        };
    };
    const innerBlock = {
        type: "rect" as const,
        x: 8,
        y: 8,
        w: 3,
        h: 3,
        lineWidth: 1,
        color: 'black',
    }
    const outerBlock = {
        type: "rect" as const,
        x: 6,
        y: 6,
        w: 7,
        h: 7,
        lineWidth: 1
    }
    const OVERSIZE_SET = [
        '1X', '2X', '3X', '4X', '5X',
        '3XL', '4XL', '5XL', '6XL', '7XL',
        '8XL', '9XL', '10XL'
    ];

    const sizeSelection = (sizeType: 'one size' | 'limited' | 'full size' | 'oversize') => {
        const { sizes } = item;
        switch (sizeType) {
            case "one size":
                return sizes.length === 1;
            case "limited":
                return sizes.length === 2;
            case "full size":
                return sizes.length >= 3;
            case "oversize":
                return sizes.length > 0 &&
                    sizes.every((size: string) =>
                        OVERSIZE_SET.includes(
                            size.trim().toUpperCase()
                        )
                    );
            default:
                return false;
        }
    };

    return {
        table: {
            widths: [550],
            body: [/* STYLE NUMBER */
                [{
                    text: [{ text: `Style no: ${item.style_no}`, bold: true }, { text: `(Description: ${item.description})`, margin: [0, 0, 0, 8] }],
                    fontSize: 14,
                    // bold: true,
                    alignment: "left",
                    margin: [0, 0, 0, 8],
                }], [
                    /* MAIN ROW */
                    {
                        columns: [
                            {
                                stack: [
                                    {
                                        columns: [
                                            /* BIG CIRCLE SECTION */
                                            {
                                                width: 200,
                                                margin: [0, -35, 0, 0],
                                                stack: [
                                                    {
                                                        canvas: [
                                                            {
                                                                type: "ellipse",
                                                                x: 100,
                                                                y: 60,
                                                                r1: 35,
                                                                r2: 30,
                                                                lineWidth: 2
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        text: item.totalQtyReceived.toString(),
                                                        alignment: "center",
                                                        margin: [0, -43, 0, 0],
                                                        fontSize: 28,
                                                        bold: true,
                                                        color: item.isOverstock ? "black" : "red"
                                                    },
                                                    {
                                                        text: `_`,
                                                        alignment: "center",
                                                        margin: [130, -50, 0, 0],
                                                        fontSize: 30,
                                                        bold: true
                                                    }
                                                ]
                                            },
                                            {
                                                text: `${generateCode(item.cost)}`,
                                                alignment: "center",
                                                fontSize: 24,
                                                characterSpacing: 4,
                                                margin: [-30, 10, 0, 0],
                                                bold: true
                                            },
                                        ]
                                    },
                                    {
                                        margin: [-20, 30, 0, 20],
                                        columns: [
                                            {
                                                width: 380,   // 🔥 control total width here
                                                stack: buildStoreRows(storeList, renderStore)
                                            }
                                        ],
                                    }
                                ]
                            },
                            /* RIGHT SIDE INFO */
                            {
                                width: "*",
                                margin: [20, 0, 0, 0],
                                stack: [
                                    {
                                        table: {
                                            widths: [60, 120],
                                            body: [
                                                [
                                                    {
                                                        text: "R -",
                                                        fontSize: 38,
                                                        bold: true,
                                                        alignment: "center",
                                                    }, {
                                                        text: `$${item.msrp.toFixed(2)}`,
                                                        fontSize: 32,
                                                        margin: [-20, 0, 0, 0],
                                                        bold: true,
                                                        alignment: "center"
                                                    }
                                                ],
                                            ],
                                        },
                                        layout: {
                                            defaultBorder: false
                                        }
                                    },
                                    {
                                        table: {
                                            widths: [80, 100],
                                            body: [
                                                [
                                                    // DOOR info
                                                    {
                                                        stack: [
                                                            {
                                                                columns: [
                                                                    {
                                                                        canvas: [
                                                                            {
                                                                                type: "ellipse" as const,
                                                                                x: 6,
                                                                                y: 6,
                                                                                r1: 6,
                                                                                r2: 6,
                                                                                lineWidth: 1
                                                                            }
                                                                        ],
                                                                        width: 13,
                                                                    },
                                                                    {
                                                                        text: "ALL DOORS",
                                                                        fontSize: 12,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ]
                                                            }, {
                                                                columns: [
                                                                    {
                                                                        canvas: [
                                                                            {
                                                                                type: "ellipse" as const,
                                                                                x: 6,
                                                                                y: 6,
                                                                                r1: 6,
                                                                                r2: 6,
                                                                                lineWidth: 1
                                                                            }
                                                                        ],
                                                                        width: 13,
                                                                    },
                                                                    {
                                                                        text: "TOP DOORS",
                                                                        fontSize: 12,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ],
                                                            }
                                                        ]
                                                    }, // Size info
                                                    {
                                                        stack: [
                                                            {
                                                                columns: [
                                                                    {
                                                                        canvas: [sizeSelection('full size') ? innerBlock : {},
                                                                        {
                                                                            type: "rect" as const,
                                                                            x: 6,
                                                                            y: 6,
                                                                            w: 7,
                                                                            h: 7,
                                                                            lineWidth: 1
                                                                        },
                                                                        ],
                                                                        width: 15,
                                                                        margin: [-5, -5, 0, 0]
                                                                    },
                                                                    {
                                                                        text: "FULL SIZE RUN",
                                                                        fontSize: 8,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ]
                                                            },
                                                            {
                                                                columns: [
                                                                    {
                                                                        canvas: [sizeSelection('limited') ? innerBlock : {},
                                                                        {
                                                                            type: "rect" as const,
                                                                            x: 6,
                                                                            y: 6,
                                                                            w: 7,
                                                                            h: 7,
                                                                            lineWidth: 1
                                                                        },
                                                                        ],
                                                                        width: 15,
                                                                        margin: [-5, -5, 0, 0]
                                                                    },
                                                                    {
                                                                        text: "LIMITED SIZES",
                                                                        fontSize: 8,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ],

                                                            },
                                                            {
                                                                columns: [
                                                                    {
                                                                        canvas: [sizeSelection('one size') ? innerBlock : {},
                                                                        {
                                                                            type: "rect" as const,
                                                                            x: 6,
                                                                            y: 6,
                                                                            w: 7,
                                                                            h: 7,
                                                                            lineWidth: 1
                                                                        },
                                                                        ],
                                                                        width: 15,
                                                                        margin: [-5, -5, 0, 0]
                                                                    },
                                                                    {
                                                                        text: "ONE SIZE",
                                                                        fontSize: 8,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ],

                                                            },
                                                            {
                                                                columns: [
                                                                    {
                                                                        canvas: [sizeSelection('oversize') ? innerBlock : {},
                                                                        {
                                                                            type: "rect" as const,
                                                                            x: 6,
                                                                            y: 6,
                                                                            w: 7,
                                                                            h: 7,
                                                                            lineWidth: 1
                                                                        },
                                                                        ],
                                                                        width: 15,
                                                                        margin: [-5, -5, 0, 0]
                                                                    },
                                                                    {
                                                                        text: "OVERSIZED",
                                                                        fontSize: 8,
                                                                        margin: [0, 0, 0, 0]
                                                                    }
                                                                ],
                                                            }
                                                        ]
                                                    }
                                                ],
                                            ]
                                        },
                                        layout: {
                                            defaultBorder: false
                                        }
                                    },
                                    {
                                        text: "$",
                                        fontSize: 38,
                                        bold: true,
                                        alignment: "left",
                                        margin: [0, 0, 0, 50]   // ADJUST THIS WHEN YOU ADD MORE STORES
                                    },
                                ]
                            }
                        ],
                        border: [false, false, false, true],
                    }],
            /* STORE SECTION */],
        },
        layout: {
            defaultBorder: false,
            // just provide bottom border for the last row of the table
        }
    };
};